import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiArrowLeft, FiCompass, FiMapPin } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { touristPlaceService } from '../services/touristPlaceService';
import { ErrorState, LoadingState, PageHero, SectionHeading } from '../components/ui/PageElements';

const TouristicPlaceDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPlace = async () => {
      try {
        setLoading(true);
        const data = await touristPlaceService.getPlaceById(id);
        setPlace(data);
        setError('');
      } catch (requestError) {
        console.error('Place detail error:', requestError);
        setError(t('design.placeDetail.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchPlace();
  }, [id, t]);

  if (loading) {
    return (
      <Layout>
        <LoadingState label={t('design.placeDetail.loading')} />
      </Layout>
    );
  }

  if (error || !place) {
    return (
      <Layout>
        <ErrorState title={t('design.placeDetail.unavailableTitle')} message={error || t('design.placeDetail.unavailableMessage')} action={<Link to="/touristic-places" className="btn-primary">{t('design.placeDetail.backToPlaces')}</Link>} />
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHero
        eyebrow={t('design.placeDetail.heroEyebrow')}
        title={place.name}
        description={t('design.placeDetail.heroDescription')}
        actions={
          <Link to="/touristic-places" className="btn-ghost">
            <FiArrowLeft className="h-4 w-4" />
            {t('design.placeDetail.backToPlaces')}
          </Link>
        }
        aside={
          <div className="soft-panel overflow-hidden">
            <div className="relative h-[360px]">
              <img
                src={place.image || '/place-placeholder.jpg'}
                alt={place.name}
                className="h-full w-full object-cover"
                onError={(event) => {
                  event.currentTarget.src = '/place-placeholder.jpg';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <p className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70">
                  <FiCompass />
                  {t('design.placeDetail.spotlight')}
                </p>
                <p className="flex items-center gap-2 text-sm text-white/85">
                  <FiMapPin />
                  {place.location || t('design.footer.location')}
                </p>
              </div>
            </div>
          </div>
        }
      />

      <section className="content-width pb-12">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="soft-panel p-6 sm:p-8">
            <SectionHeading
              eyebrow={t('design.placeDetail.storyEyebrow')}
              title={t('design.placeDetail.storyTitle')}
              description={t('design.placeDetail.storyDescription')}
            />
            <div className="rounded-[28px] border border-stone-200 bg-stone-50/80 p-6">
              <p className="text-sm leading-8 text-slate-700">{place.description || t('design.placeDetail.descriptionFallback')}</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="soft-panel p-6 sm:p-8">
              <p className="eyebrow">{t('hotels.location')}</p>
              <div className="mt-5 rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="flex items-center gap-2 text-slate-700">
                  <FiMapPin className="text-teal-700" />
                  {place.location || t('design.footer.location')}
                </p>
              </div>
            </div>
            <div className="soft-panel p-6 sm:p-8">
              <p className="eyebrow">{t('common.note')}</p>
              <h3 className="mt-4 text-2xl font-semibold text-slate-900">{t('design.placeDetail.noteTitle')}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">
                {t('design.placeDetail.noteDescription')}
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TouristicPlaceDetailPage;
